// components/ProtectedRoute.tsx
import { ReactNode } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
	children: ReactNode;
	redirectTo?: string; // default '/login'
}

const Spinner = () => <div aria-busy="true"></div>;

const ProtectedRoute = ({ children, redirectTo = "/" }: ProtectedRouteProps) => {
	const { user, loadingUser } = useAuth();
	const location = useLocation();

	// While auth is bootstrapping — render a placeholder / spinner
	if (loadingUser) {
		return <Spinner />;
	}

	// Not authenticated -> redirect to login and preserve the location user tried to access
	if (!user) {
		return <Navigate to={redirectTo} replace state={{ from: location }} />;
	}

	// Authenticated -> render children
	return <>{children}</>;
};

export default ProtectedRoute;

interface AdminProtectedRouteProps {
	children: ReactNode;
	redirectTo?: string; // default '/login'
}

export const AdminProtectedRoute = ({ children, redirectTo = "/" }: AdminProtectedRouteProps) => {
	const { user, loadingUser } = useAuth();
	const location = useLocation();

	console.log("AdminProtectedRoute: loadingUser =", loadingUser, "user =", user);

	// While auth is bootstrapping — render a placeholder / spinner
	if (loadingUser) {
		return <Spinner />;
	}

	// Not authenticated -> redirect to login
	if (!user) {
		console.log("AdminProtectedRoute: No user, redirecting to", redirectTo);
		return <Navigate to={redirectTo} replace state={{ from: location }} />;
	}

	// Not an admin -> redirect to home
	const hasAdminPermission = user.permissions?.includes("Admin Login");
	if (!user.is_staff && !user.is_superuser && !hasAdminPermission) {
		console.log("AdminProtectedRoute: User is not admin and lacks Admin Login permission, redirecting to /");
		return <Navigate to="/" replace />;
	}

	// Authenticated and is admin -> render children
	console.log("AdminProtectedRoute: Access granted");
	return <>{children}</>;
};

export const InternalProtectedRoute = ({ children, redirectTo = "/" }: AdminProtectedRouteProps) => {
	const { user, loadingUser } = useAuth();
	const location = useLocation();

	if (loadingUser) {
		return <Spinner />;
	}

	if (!user) {
		return <Navigate to={redirectTo} replace state={{ from: location }} />;
	}

	if (user.user_type?.toUpperCase() !== "INTERNAL") {
		console.log("InternalProtectedRoute: User is not INTERNAL, redirecting to /");
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
};
